import { AdElementImage } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { FC, useMemo } from 'react';

type ImageElementData = AdElementImage['data'] & { isBrandLogo?: boolean };

type Props = {
  id: string;
  data: ImageElementData;
  position: AdElementImage['position'];
  size: AdElementImage['size'];
  rotation?: AdElementImage['rotation'];
  isPreview?: boolean;
  animationStyle?: ElementAnimationStyle;
};

const ImageElement: FC<Props> = ({
  id,
  position,
  size,
  rotation,
  data,
  isPreview,
  animationStyle,
}) => {
  const content = useMemo(() => {
    return data.content.startsWith('https://cdn.fresh8.co') && size.width
      ? `${data.content}?width=${size.width * 2}`
      : data.content;
  }, [data.content, size.width]);

  const src = useMemo(() => {
    if (content) {
      return content;
    }
    if (isPreview) {
      return `https://placehold.co/${size.width}x${size.height}`;
    }
    throw new Error(`Image element {${id}} content is empty`);
  }, [content, id, isPreview, size.width, size.height]);

  // Animation transforms wrap the element's own rotation so slide offsets stay
  // in screen axes and scale is unaffected by the rotation.
  const transform =
    [animationStyle?.transform, rotation ? `rotate(${rotation}rad)` : undefined]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <img
      id={id}
      src={src}
      width={size.width}
      height={size.height}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        objectFit: data.isBrandLogo ? 'contain' : 'cover',
        zIndex: position.z,
        transform,
        opacity: animationStyle?.opacity,
      }}
    />
  );
};

ImageElement.displayName = 'ImageElement';

export default ImageElement;
